/* eslint-disable sonarjs/max-switch-cases */
import { Reducer } from 'redux';
import { PaymentState, PaymentActions, PaymentActionTypes } from './payment.types';
import { PaymentDetailsModel, CardPaymentStateModel, IfcPaymentStateModel } from 'models/Payment';

const initialState: PaymentState = {
    isPaymentLoading: false,
    isPaymentCalculatorInitializing: false,
    paymentDetails: new PaymentDetailsModel(),
    cardPaymentState: null,
    ifcPaymentState: null,
    isPaymentUpdating: false,
    isPaymentTypeUpdating: false,
    isCardPaymentProcessing: false,
    cardPaymentError: null,
    paymentSuccessfullyCompleted: false,
    successfulPaymentResult: null,
    isPaypalPaymentProcessing: false,
    isPaypalPaymentSetupInProgress: false,
    paypalPaymentCode: null,
    paypalAuthSuccessfullResult: null,
    paypalPaymentError: null,
    isPaypalPaymentCompleteProcessing: false,
    isIfcFormSubmitted: false,
    paymentSession: null,
    isPaymentSessionLoading: false
};

export const paymentReducer: Reducer<PaymentState, PaymentActions> = (state = initialState, action) => {
    switch (action.type) {
        case PaymentActionTypes.CHANGE_PAYMENT_TYPE_START: {
            return {
                ...state,
                isPaymentTypeUpdating: true,
                paymentDetails: {
                    ...state.paymentDetails,
                    paymentType: action.paymentType
                }
            };
        }

        case PaymentActionTypes.CHANGE_PAYMENT_TYPE_SUCCESS:
        case PaymentActionTypes.CHANGE_PAYMENT_TYPE_ERROR: {
            return {
                ...state,
                isPaymentTypeUpdating: false
            };
        }

        case PaymentActionTypes.LOAD_PAYMENT: {
            return { ...state, isPaymentLoading: true };
        }

        case PaymentActionTypes.LOAD_PAYMENT_SUCCESS: {
            return {
                ...state,
                isPaymentLoading: false,
                paymentDetails: action.paymentDetails
            };
        }

        case PaymentActionTypes.LOAD_PAYMENT_ERROR: {
            return {
                ...state,
                isPaymentLoading: false
            };
        }

        case PaymentActionTypes.INITIALIZE_PAYMENT_CALCULATOR_START: {
            return { ...state, isPaymentCalculatorInitializing: true };
        }

        case PaymentActionTypes.INITIALIZE_PAYMENT_CALCULATOR_SUCCESS: {
            return {
                ...state,
                isPaymentCalculatorInitializing: false,
                paymentDetails: action.paymentDetails,
                cardPaymentState: action.cardPaymentState,
                ifcPaymentState: action.ifcPaymentState,
                isIfcFormSubmitted: action.isIfcFormSubmitted
            };
        }

        case PaymentActionTypes.INITIALIZE_PAYMENT_CALCULATOR_ERROR: {
            return {
                ...state,
                isPaymentCalculatorInitializing: false
            };
        }

        case PaymentActionTypes.UPDATE_CARD_PAYMENT_START: {
            return {
                ...state,
                isPaymentUpdating: true
            };
        }

        case PaymentActionTypes.UPDATE_CARD_PAYMENT_SUCCESS: {
            return {
                ...state,
                paymentDetails: action.paymentDetails,
                cardPaymentState: action.cardPaymentState,
                isPaymentUpdating: false
            };
        }

        case PaymentActionTypes.UPDATE_CARD_PAYMENT_ERROR: {
            return {
                ...state,
                isPaymentUpdating: false,
                // to override internal component state by replacing object in props
                cardPaymentState: { ...state.cardPaymentState } as CardPaymentStateModel,
                paymentDetails: { ...state.paymentDetails } as PaymentDetailsModel
            };
        }

        case PaymentActionTypes.UPDATE_IFC_PAYMENT_START: {
            return {
                ...state,
                isPaymentUpdating: true
            };
        }

        case PaymentActionTypes.UPDATE_IFC_PAYMENT_SUCCESS: {
            return {
                ...state,
                paymentDetails: action.paymentDetails,
                ifcPaymentState: action.ifcPaymentState,
                isPaymentUpdating: false
            };
        }

        case PaymentActionTypes.UPDATE_IFC_PAYMENT_ERROR: {
            return {
                ...state,
                isPaymentUpdating: false,
                // to override internal component state by replacing object in props
                ifcPaymentState: { ...state.ifcPaymentState } as IfcPaymentStateModel,
                paymentDetails: { ...state.paymentDetails } as PaymentDetailsModel
            };
        }

        case PaymentActionTypes.CARD_PAYMENT_COMPLETE_START: {
            return {
                ...state,
                isCardPaymentProcessing: true,
                cardPaymentError: null,
                paymentSuccessfullyCompleted: false
            };
        }

        case PaymentActionTypes.CARD_PAYMENT_COMPLETE_SUCCESS: {
            return {
                ...state,
                isCardPaymentProcessing: false,
                cardPaymentError: null,
                paymentSuccessfullyCompleted: true,
                successfulPaymentResult: action.cardPaymentSuccessfulResult
            };
        }

        case PaymentActionTypes.CARD_PAYMENT_COMPLETE_ERROR: {
            return {
                ...state,
                isCardPaymentProcessing: false,
                cardPaymentError: action.error
            };
        }

        case PaymentActionTypes.REMOVE_CARD_PAYMENT_ERROR_MESSAGE: {
            return {
                ...state,
                cardPaymentError: null
            };
        }

        case PaymentActionTypes.CARD_PAYMENT_REJECT_SUCCESS: {
            return {
                ...state,
                cardPaymentError: action.error
            };
        }

        case PaymentActionTypes.REMOVE_PAYMENT_RESULT: {
            return {
                ...state,
                successfulPaymentResult: null
            };
        }

        case PaymentActionTypes.PAYPAL_PAYMENT_START: {
            return {
                ...state,
                successfullPaymentResult: null,
                paymentSuccessfullyCompleted: false,
                isPaypalPaymentProcessing: true,
                paypalPaymentError: null
            };
        }

        case PaymentActionTypes.PAYPAL_PAYMENT_SUCCESS: {
            return {
                ...state,
                isPaypalPaymentProcessing: false,
                paymentSuccessfullyCompleted: true,
                successfulPaymentResult: action.paypalPaymentResult
            };
        }

        // is created on each error in paypal payment process (splitted in several sagas): from clicking on login button, to receiving results of transaction
        case PaymentActionTypes.PAYPAL_PAYMENT_ERROR: {
            return {
                ...state,
                isPaypalPaymentProcessing: false,
                isPaypalPaymentSetupInProgress: false,
                paypalPaymentCode: null,
                paypalAuthSuccessfullResult: null,
                paypalPaymentError: action.error
            };
        }

        case PaymentActionTypes.PAYPAL_SETUP_PAYMENT_START: {
            return {
                ...state,
                isPaypalPaymentSetupInProgress: true,
                paypalPaymentCode: null
            };
        }

        case PaymentActionTypes.PAYPAL_SETUP_PAYMENT_SUCCESS: {
            return {
                ...state,
                isPaypalPaymentSetupInProgress: false,
                paypalPaymentCode: action.paymentCode,
            };
        }

        case PaymentActionTypes.PAYPAL_SETUP_PAYMENT_ERROR: {
            return {
                ...state,
                isPaypalPaymentSetupInProgress: false
            };
        }

        case PaymentActionTypes.PAYPAL_SET_AUTH_RESULT: {
            return {
                ...state,
                paypalAuthSuccessfullResult: action.paypalAuthResult
            };
        }

        case PaymentActionTypes.REMOVE_PAYPAL_PAYMENT_ERROR_MESSAGE: {
            return {
                ...state,
                paypalPaymentError: null
            };
        }

        case PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT_START: {
            return {
                ...state,
                isPaypalPaymentCompleteProcessing: true
            };
        }

        case PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT_SUCCESS:
        case PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT_ERROR: {
            return {
                ...state,
                isPaypalPaymentCompleteProcessing: false
            };
        }

        case PaymentActionTypes.CREATE_PAYMENT_SESSION_START: {
            return {
                ...state,
                paymentSession: null,
                isPaymentSessionLoading: true
            };
        }

        case PaymentActionTypes.CREATE_PAYMENT_SESSION_SUCCESS: {
            return {
                ...state,
                paymentSession: action.paymentSession,
                isPaymentSessionLoading: false
            };
        }

        case PaymentActionTypes.CREATE_PAYMENT_SESSION_ERROR: {
            return {
                ...state,
                paymentSession: null,
                isPaymentSessionLoading: false
            };
        }

        case PaymentActionTypes.VALIDATE_BEFORE_PAYMENT_START: {
            return {
                ...state,
                isCardPaymentProcessing: true
            };
        }

        case PaymentActionTypes.VALIDATE_BEFORE_PAYMENT_SUCCESS:
        case PaymentActionTypes.VALIDATE_BEFORE_PAYMENT_ERROR: {
            return {
                ...state,
                isCardPaymentProcessing: false
            };
        }

        default: {
            return state;
        }
    }
};